import styled, { css } from 'styled-components';
import { Typography } from '@/components/UI/Typography/Typography.styles';
import { SkeletonLoader } from '@/components/SkeletonLoader/SkeletonLoader';
import { SmallChevronIcon } from '@/components/UI/Icons/Icons';

function getBaseImageBoxStyles(isHorizontalView?: boolean) {
  return isHorizontalView
    ? css`
        width: 60px;
        height: 60px;
        border-radius: 16px;
        margin-bottom: 5px;

        @media (min-width: 950px) {
          width: 95px;
          height: 95px;
          border-radius: 21px;
        }
      `
    : css`
        width: 40px;
        height: 40px;
        border-radius: 7px;
        padding: 2px;
      `;
}

function getImageStyles(
  isHorizontalView?: boolean,
  isAllCategoriesItem?: boolean,
  isStoresIcon?: boolean
) {
  switch (true) {
    case !isHorizontalView:
      return css`
        width: 100%;
      `;
    case isAllCategoriesItem:
      return css`
        width: 26px;
        height: 26px;
      `;
    case isStoresIcon:
      return css`
        width: 60px;
        height: 60px;

        @media (min-width: 950px) {
          width: 95px;
          height: 95px;
        }
      `;
    default:
      return css`
        width: 50px;
        height: 50px;

        @media (min-width: 950px) {
          width: 70px;
          height: 70px;
        }
      `;
  }
}

const HorizontalGridView = css`
  width: min-content;
  min-width: 80px;

  @media (min-width: 950px) {
    min-width: 105px;
  }
`;

const DefaultGridView = css`
  display: grid;
  height: 60px;
  grid-template-columns: 40px auto 18px;
  gap: 15px;
  overflow: hidden;
`;

export const ImgBox = styled.div.attrs({ className: 'img-box' })<{
  $isHorizontalView?: boolean;
  $isAllCategoriesItem?: boolean;
  $isStoresIcon?: boolean;
}>`
  ${({ $isHorizontalView }) => getBaseImageBoxStyles($isHorizontalView)};
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${({ theme }) => theme.palette.secondary.background};
  overflow: hidden;

  img {
    object-fit: contain;
    ${({ $isHorizontalView, $isAllCategoriesItem, $isStoresIcon }) =>
      getImageStyles($isHorizontalView, $isAllCategoriesItem, $isStoresIcon)}
  }
`;

export const Texts = styled.div`
  overflow: hidden;
`;

export const GridItemTitle = styled(Typography.Caption)`
  text-align: center;
  color: ${({ theme }) => theme.palette.primary.labels};

  @media (min-width: 950px) {
    font-size: 14px;
  }
`;

export const ItemTitle = styled(Typography.Body)`
  margin-bottom: 5px;
`;

export const ItemDescription = styled(Typography.Caption)`
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  max-width: 100%;
`;

export const ImageSkeletonLoader = styled(SkeletonLoader)<{
  $isHorizontalView?: boolean;
}>`
  ${({ $isHorizontalView }) => getBaseImageBoxStyles($isHorizontalView)};
`;

export const Box = styled.div<{
  $isHorizontalView?: boolean;
  $hideOnDesktop?: boolean;
}>`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;

  ${({ $isHorizontalView }) =>
    $isHorizontalView ? HorizontalGridView : DefaultGridView}

  @media (hover: hover) {
    &:hover {
      .img-box {
        background-color: ${({ theme }) => theme.palette.tertiary.background};
      }
      p {
        color: ${({ theme }) => theme.palette.brand.main};
      }
    }
  }

  @media (min-width: 950px) {
    display: ${({ $hideOnDesktop }) => ($hideOnDesktop ? 'none' : 'flex')};
  }
`;

export const TextSkeletonLoader = styled(SkeletonLoader)`
  &:not(:last-child) {
    margin-bottom: 3px;
  }
`;

export const CategoryChevronIcon = styled(SmallChevronIcon)`
  width: 8px;
  height: 14px;
  color: ${({ theme }) => theme.palette.secondary.labels};
`;

export const HorizontalTitleLoaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
`;
